import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { BusinessTypeDistribution as BusinessTypeDistributionType } from '../../../types/utility.types';

interface BusinessTypeDistributionProps {
  data: BusinessTypeDistributionType[];
  isLoading: boolean;
}

// Color mapping matching actual backend business type enum values
const BUSINESS_TYPE_COLORS: Record<string, string> = {
  'Limited Company': '#0088FE', // Blue
  'Sole Trader': '#00C49F', // Green
  'Charity / Trust / Community Org': '#FFBB28', // Yellow
  'default': '#9e9e9e' // Gray for unknown types
};

const getBusinessTypeColor = (type: string): string => {
  return BUSINESS_TYPE_COLORS[type] || BUSINESS_TYPE_COLORS.default;
};

const BusinessTypeDistribution: React.FC<BusinessTypeDistributionProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Business Type Distribution
          </Typography>
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography>Loading...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Filter out any items with count 0 and sort by count (descending)
  const chartData = data
    .filter(item => item.count > 0)
    .sort((a, b) => b.count - a.count);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Business Type Distribution
        </Typography>
        {chartData.length > 0 ? (
          <Box sx={{ height: 300 }}>
            <PieChart
              series={[
                {
                  data: chartData.map((item, index) => ({
                    id: index,
                    value: item.count,
                    label: item.type,
                    color: getBusinessTypeColor(item.type),
                  })),
                  highlightScope: { faded: 'global', highlighted: 'item' },
                  faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                },
              ]}
              height={300}
              margin={{ right: 200 }}
              slotProps={{
                legend: {
                  direction: 'column',
                  position: { vertical: 'middle', horizontal: 'right' },
                  padding: 0,
                  itemMarkWidth: 15,
                  itemMarkHeight: 15,
                  markGap: 8,
                  itemGap: 8,
                },
              }}
              sx={{
                '& .MuiPopper-root': {
                  '& .MuiChartsTooltip-root': {
                    background: 'rgba(30, 41, 59, 0.95) !important',
                    backdropFilter: 'blur(12px) !important',
                    border: '1px solid rgba(255, 255, 255, 0.1) !important',
                    borderRadius: '8px !important',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3) !important',
                  },
                  '& .MuiChartsTooltip-table': {
                    color: 'rgba(255, 255, 255, 0.9) !important',
                  },
                },
              }}
            />
          </Box>
        ) : (
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography>No data available</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default BusinessTypeDistribution;
